const PARCEL_MACHINE_SUFFIX = "_parcel_machine";
const PICKUP_POINT_SUFFIX = "_pickup_point";
const POST_OFFICE_SUFFIX = "_post_office";

const KNOWN_PROVIDERS = ["dpd", "itella", "omniva"] as const;

const LOCATION_METHOD_SUFFIXES = [
  PARCEL_MACHINE_SUFFIX,
  PICKUP_POINT_SUFFIX,
  POST_OFFICE_SUFFIX,
] as const;

export type ParcelProvider = (typeof KNOWN_PROVIDERS)[number];

export function isParcelMachineMethod(methodCode?: string | null): boolean {
  if (!methodCode) {
    return false;
  }
  return (
    methodCode === "parcel_machine" || methodCode.endsWith(PARCEL_MACHINE_SUFFIX)
  );
}

/** Post offices / carrier pickup points (not parcel lockers). */
export function isPickupPointMethod(methodCode?: string | null): boolean {
  if (!methodCode) {
    return false;
  }
  return (
    methodCode === "pickup_point" ||
    methodCode.endsWith(PICKUP_POINT_SUFFIX) ||
    methodCode.endsWith(POST_OFFICE_SUFFIX)
  );
}

/** Parcel lockers and carrier pickup points share the same location list UI. */
export function isDeliveryLocationMethod(methodCode?: string | null): boolean {
  return isParcelMachineMethod(methodCode) || isPickupPointMethod(methodCode);
}

function resolveProviderFromMethodCode(methodCode: string): string | null {
  if (
    methodCode === "parcel_machine" ||
    methodCode === "pickup_point" ||
    methodCode === "post_office"
  ) {
    return null;
  }
  for (const suffix of LOCATION_METHOD_SUFFIXES) {
    if (methodCode.endsWith(suffix)) {
      return methodCode.slice(0, -suffix.length);
    }
  }
  return null;
}

function resolveProviderFromLocationsUrl(locationsUrl: string): string | null {
  const filename =
    locationsUrl.split("/").pop()?.split("?")[0]?.toLowerCase() ?? "";
  const baseName = filename.replace(/\.[^.]+$/, "");

  for (const provider of KNOWN_PROVIDERS) {
    if (
      baseName === provider ||
      baseName.startsWith(`${provider}_`) ||
      baseName.startsWith(`${provider}-`)
    ) {
      return provider;
    }
  }

  const lowerUrl = locationsUrl.toLowerCase();
  for (const provider of KNOWN_PROVIDERS) {
    if (lowerUrl.includes(`/${provider}`)) {
      return provider;
    }
  }

  return null;
}

/** Resolves dpd | itella | omniva for location list formatting and cache keys. */
export function resolveParcelProvider(
  methodCode?: string,
  locationsUrl?: string
): string {
  if (methodCode) {
    const fromMethod = resolveProviderFromMethodCode(methodCode);
    if (fromMethod) {
      return fromMethod;
    }
  }

  if (locationsUrl) {
    const fromUrl = resolveProviderFromLocationsUrl(locationsUrl);
    if (fromUrl) {
      return fromUrl;
    }
  }

  let fallback = methodCode ?? "";
  for (const suffix of LOCATION_METHOD_SUFFIXES) {
    fallback = fallback.replace(suffix, "");
  }
  return fallback;
}
